import React, { useState, useCallback, useEffect } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { login, getCaptcha, verifyCaptcha } from '../services/admin';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<{ question: string; answer: string }>({ question: '', answer: '' });

  const refreshCaptcha = useCallback(async () => {
    try {
      const response = await getCaptcha();
      if (response.code === 0 && response.data) {
        setCaptcha(response.data);
      } else {
        message.error('获取验证码失败');
      }
    } catch (error) {
      message.error('获取验证码失败');
    }
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const onFinish = async (values: { username: string; password: string; captcha: string }) => {
    if (loading) return;
    
    // 验证验证码
    try {
      const verifyResponse = await verifyCaptcha({ 
        id: captcha.answer, // 使用验证码的标识符
        answer: values.captcha // 用户输入的答案
      });
      if (verifyResponse.code !== 0 || !verifyResponse.data) {
        message.error('验证码错误');
        refreshCaptcha();
        return;
      }
    } catch (error) {
      message.error('验证码验证失败');
      refreshCaptcha();
      return;
    }

    setLoading(true);

    try {
      console.log('Starting login process...');
      const response = await login({ username: values.username, password: values.password });
      console.log('Login response:', response);

      if (response && response.code === 0 && response.data) {
        console.log('Login successful, saving auth data...');
        const { token, admin } = response.data;
        localStorage.setItem('admin_token', token);
        localStorage.setItem('admin_user', JSON.stringify(admin));
        
        console.log('Updating auth context...');
        authLogin(token, admin);
        
        message.success('登录成功');
        
        console.log('Navigating to dashboard...');
        setTimeout(() => {
          navigate('/users', { replace: true });
        }, 100);
      } else {
        console.error('Invalid response format:', response);
        message.error(response?.message || '登录响应数据格式错误');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.message || '登录失败，请检查网络连接');
    } finally {
      setLoading(false);
      refreshCaptcha();
    }
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      background: '#f0f2f5'
    }}>
      <Card style={{ width: 400 }}>
        <h2 style={{ textAlign: 'center', marginBottom: 24 }}>管理员登录</h2>
        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="用户名" 
              size="large"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              size="large"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="captcha"
            rules={[{ required: true, message: '请输入验证码' }]}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="验证码"
                size="large"
                disabled={loading}
                style={{ flex: 1 }}
              />
              <div 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '0 12px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
                onClick={refreshCaptcha}
              >
                <span style={{ marginRight: '8px' }}>{captcha.question}</span>
                <ReloadOutlined />
              </div>
            </div>
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              size="large"
              loading={loading}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login; 